import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { mediaUrl } from "@/lib/api";
import { Surface, Pill, AButton, Skeleton, ConditionDots } from "@/components/ios";
import {
  ChevronLeft, Check, Pencil, X, Sparkles, ShieldCheck, Send, FileSignature,
  Image as ImageIcon, Fingerprint, ExternalLink, Copy, Eye, FileArchive, Map, GitCompare, Download, Mic, Play, Volume2, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SEV_TONE = { none: "neutral", minor: "warning", moderate: "warning", major: "danger" };
const STATUS_TONE = { draft: "warning", agent_signed: "accent", issued_to_tenant: "accent", tenant_responded: "success", final: "success" };

export default function ReportReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const [rep, setRep] = useState(null);
  const [evidence, setEvidence] = useState(null); // {item, media:[]}
  const [editItem, setEditItem] = useState(null);
  const [signOpen, setSignOpen] = useState(false);
  const [signName, setSignName] = useState("");
  const [issued, setIssued] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [playingClip, setPlayingClip] = useState(null);

  const playClip = async (mediaId) => {
    try {
      setPlayingClip(mediaId);
      const { data } = await api.get(`/media/${mediaId}/audio`, { responseType: "blob" });
      const audio = new Audio(URL.createObjectURL(data));
      audio.onended = () => setPlayingClip(null);
      audio.onerror = () => setPlayingClip(null);
      await audio.play();
    } catch (e) { setPlayingClip(null); }
  };

  const load = useCallback(() => api.get(`/reports/${id}`).then(({ data }) => setRep(data)), [id]);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get(`/reports/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(rep?.property?.address || "report").split(",")[0].trim().replace(/\s+/g, "-")}-report.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) { toast.error("Could not generate PDF"); }
    finally { setDownloading(false); }
  };

  const downloadEvidencePack = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get(`/reports/${id}/evidence-pack`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(rep?.property?.address || "report").split(",")[0].trim().replace(/\s+/g, "-")}-evidence-pack.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Evidence pack downloaded");
    } catch (e) { toast.error("Could not generate evidence pack"); }
    finally { setDownloading(false); }
  };


  const review = async (item, action, payload = {}) => {
    try {
      await api.put(`/reports/${id}/items/${item.item_key}`, { action, ...payload });
      if (action === "confirm") toast.success("Confirmed");
      if (action === "reject") toast("Marked not sighted");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const openEvidence = async (item) => {
    const media = [];
    for (const ref of item.evidence_refs || []) {
      try { const { data } = await api.get(`/media/${ref.media_id}`); media.push(data); } catch (e) {}
    }
    setEvidence({ item, media });
  };

  const doSign = async () => {
    try {
      await api.post(`/reports/${id}/sign`);
      toast.success("Report signed", { icon: <FileSignature size={16} /> });
      setSignOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sign failed"); }
  };

  const doIssue = async () => {
    try {
      const { data } = await api.post(`/reports/${id}/issue`);
      setIssued(data);
      toast.success("Issued to tenant & owner");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Issue failed"); }
  };

  if (!rep) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;

  const locked = rep.status !== "draft";
  const origin = window.location.origin;
  const tokenLink = (t, base) => `${origin}/${base}/${t}`;

  return (
    <div className="pb-16">
      <button onClick={() => nav("/inspections")} className="flex items-center gap-1 text-sm text-secondary mb-4"><ChevronLeft size={16} /> Inspections</button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: "var(--text)" }}>{rep.property?.address}</h1>
          <p className="text-sm text-secondary capitalize">{rep.schema?.type_label} · {rep.property?.state}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Pill tone={STATUS_TONE[rep.status]}>{rep.status.replace(/_/g, " ")}</Pill>
            {rep.onsite?.verified && (
              <Pill tone="success" data-testid="onsite-verified" title={`Device was ${rep.onsite.distance_m}m from the property address when captured`}>
                <MapPin size={11} /> On-site verified
              </Pill>
            )}
            {rep.onsite && !rep.onsite.verified && (
              <Pill tone="warning" data-testid="onsite-unverified" title={`Device was ${rep.onsite.distance_m}m away — outside the ${rep.onsite.radius_m}m radius`}>
                <MapPin size={11} /> Location not verified
              </Pill>
            )}
            <span className="flex items-center gap-1 text-xs text-secondary"><Fingerprint size={12} /> {rep.version_hash?.slice(0, 12)}…</span>
          </div>
        </div>
        <div className="flex gap-2">
          {rep.status === "draft" && <AButton testid="sign-report-btn" onClick={() => { setSignName(""); setSignOpen(true); }}><FileSignature size={15} className="inline mr-1.5" /> Approve & sign</AButton>}
          {rep.status === "agent_signed" && <AButton testid="issue-report-btn" onClick={doIssue}><Send size={15} className="inline mr-1.5" /> Issue to tenant</AButton>}
          <AButton variant="text" testid="download-pdf-btn" disabled={downloading} onClick={downloadPdf}><Download size={15} className="inline mr-1.5" /> {downloading ? "Preparing…" : "Download PDF"}</AButton>
          <AButton variant="text" testid="download-evidence-pack-btn" disabled={downloading} onClick={downloadEvidencePack}><FileArchive size={15} className="inline mr-1.5" /> Evidence pack (PDF)</AButton>
          <AButton variant="text" testid="floorplan-btn" onClick={() => nav(`/reports/${id}/plan`)}><Map size={15} className="inline mr-1.5" /> Floor plan</AButton>
          {rep.report_type === "exit" && <AButton variant="text" testid="comparison-btn" onClick={() => nav(`/reports/${id}/comparison`)}><GitCompare size={15} className="inline mr-1.5" /> Entry vs exit</AButton>}
          {rep.status !== "draft" && <AButton variant="text" testid="evidence-pack-btn" onClick={() => nav(`/reports/${id}/evidence`)}><FileArchive size={15} className="inline mr-1.5" /> Evidence pack</AButton>}
        </div>
      </div>

      {/* AI summary */}
      <Surface className="p-5 mb-5">
        <div className="flex items-center gap-2 text-sm font-medium text-secondary mb-2"><Sparkles size={15} className="accent-text" /> AI-drafted owner summary · grounded in findings only</div>
        <p className="leading-relaxed" style={{ color: "var(--text)" }}>{rep.overall_summary}</p>
        <div className="mt-3 text-xs text-secondary">Drafting model: {rep.model_version?.drafting} · Vision: {rep.model_version?.vision}</div>
      </Surface>

      {issued && (
        <Surface className="p-5 mb-5" data-testid="issued-links">
          <div className="font-semibold mb-3" style={{ color: "var(--text)" }}>Shareable report links</div>
          {[["Tenant portal", tokenLink(issued.tenant_token, "portal")], ["Owner report", tokenLink(issued.owner_token, "owner")]].map(([label, link]) => (
            <div key={label} className="flex items-center justify-between gap-2 py-2 border-t" style={{ borderColor: "var(--border)" }}>
              <div className="min-w-0">
                <div className="text-sm font-medium" style={{ color: "var(--text)" }}>{label}</div>
                <div className="text-xs text-secondary truncate max-w-[220px]">{link}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }} className="p-2 rounded-lg" style={{ background: "var(--surface-2)" }}><Copy size={14} className="accent-text" /></button>
                <a href={link} target="_blank" rel="noreferrer" className="p-2 rounded-lg" style={{ background: "var(--surface-2)" }}><ExternalLink size={14} className="accent-text" /></a>
              </div>
            </div>
          ))}
          <div className="text-xs text-secondary mt-2">Tenant response window closes {new Date(issued.response_deadline).toLocaleDateString("en-AU")}.</div>
        </Surface>
      )}

      {rep.tenant_response && (
        <Surface className="p-5 mb-5">
          <div className="font-semibold mb-2" style={{ color: "var(--text)" }}>Tenant response (structured deltas)</div>
          {Object.entries(rep.tenant_response.items).map(([k, v]) => (
            <div key={k} className="text-sm py-1.5 border-t flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
              <Pill tone={v.agreement === "agree" ? "success" : "danger"}>{v.agreement}</Pill>
              <span className="text-secondary">{k}</span>
              {v.comment && <span style={{ color: "var(--text)" }}>— {v.comment}</span>}
            </div>
          ))}
        </Surface>
      )}

      {/* Sections */}
      {rep.report_json.sections.map((sec) => {
        const items = sec.items.filter((i) => !i.not_sighted || i.comment !== "Not sighted." || i.evidence_refs?.length);
        const shown = sec.items;
        return (
          <div key={sec.key} className="mb-4">
            <div className="px-1 pb-2 text-xs font-semibold uppercase tracking-wide text-secondary">{sec.title}</div>
            <Surface className="overflow-hidden">
              <div className="divide-y" style={{ borderColor: "var(--border)" }}>
                {shown.map((it) => (
                  <div key={it.item_key} className="p-4" data-testid={`review-item-${it.item_key}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium" style={{ color: "var(--text)" }}>{it.label}</span>
                          <ConditionDots condition={it.condition} />
                          {it.ai_generated && <Pill tone="accent"><Sparkles size={10} /> AI</Pill>}
                          {it.voice_derived && <Pill tone="success" data-testid={`voice-${it.item_key}`} title={it.spoken_note ? `Linked & described from your spoken commentary` : "Linked from your spoken commentary"}><Mic size={10} /> From voice</Pill>}
                          {it.severity !== "none" && <Pill tone={SEV_TONE[it.severity]}>{it.severity}</Pill>}
                          {it.fair_wear === "beyond_fair_wear" && <Pill tone="danger" data-testid={`fairwear-${it.item_key}`}>Beyond fair wear</Pill>}
                          {it.fair_wear === "fair_wear" && <Pill tone="neutral" data-testid={`fairwear-${it.item_key}`}>Fair wear &amp; tear</Pill>}
                          {it.review_status === "confirm" && <Pill tone="success"><Check size={10} /> confirmed</Pill>}
                          {it.review_status === "edit" && <Pill tone="warning">edited</Pill>}
                          {it.review_status === "reject" && <Pill tone="danger">rejected</Pill>}
                        </div>
                        <p className="text-sm mt-1.5" style={{ color: it.comment === "Not sighted." ? "var(--text-secondary)" : "var(--text)" }}>{it.comment}</p>
                        {it.spoken_verbatim && (
                          <div className="mt-2 rounded-lg px-3 py-2 border text-xs" data-testid={`verbatim-${it.item_key}`}
                            style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
                            <div className="flex items-center gap-1.5 font-semibold text-secondary uppercase tracking-wide" style={{ fontSize: 10 }}>
                              <Mic size={11} className="accent-text" /> Inspector said (verbatim, on-site)
                            </div>
                            <p className="mt-1 italic" style={{ color: "var(--text)" }}>&ldquo;{it.spoken_verbatim}&rdquo;</p>
                            {it.spoken_audio?.media_id && (
                              <button data-testid={`play-clip-${it.item_key}`} onClick={() => playClip(it.spoken_audio.media_id)}
                                className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold accent-text">
                                {playingClip === it.spoken_audio.media_id ? <Volume2 size={13} /> : <Play size={12} />}
                                {playingClip === it.spoken_audio.media_id ? "Playing…" : "Play original clip"}
                                {it.spoken_audio.sha256 && <span className="text-secondary font-normal" style={{ fontSize: 10 }}>· sha256 {it.spoken_audio.sha256.slice(0, 8)}…</span>}
                              </button>
                            )}
                          </div>
                        )}
                        {it.evidence_refs?.length > 0 && (
                          <button data-testid={`evidence-${it.item_key}`} onClick={() => openEvidence(it)}
                            className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold accent-text">
                            <ImageIcon size={13} /> {it.evidence_refs.length} evidence · tap to view
                          </button>
                        )}
                      </div>
                      {!locked && (
                        <div className="flex gap-1 shrink-0">
                          <motion.button whileTap={{ scale: 0.9 }} data-testid={`confirm-${it.item_key}`} onClick={() => review(it, "confirm")}
                            className="h-8 w-8 rounded-full grid place-items-center" style={{ background: "color-mix(in srgb, var(--success) 15%, transparent)" }}>
                            <Check size={15} style={{ color: "var(--success)" }} /></motion.button>
                          <motion.button whileTap={{ scale: 0.9 }} data-testid={`edit-${it.item_key}`} onClick={() => setEditItem({ ...it })}
                            className="h-8 w-8 rounded-full grid place-items-center" style={{ background: "var(--accent-soft)" }}>
                            <Pencil size={14} className="accent-text" /></motion.button>
                          <motion.button whileTap={{ scale: 0.9 }} data-testid={`reject-${it.item_key}`} onClick={() => review(it, "reject")}
                            className="h-8 w-8 rounded-full grid place-items-center" style={{ background: "color-mix(in srgb, var(--danger) 15%, transparent)" }}>
                            <X size={15} style={{ color: "var(--danger)" }} /></motion.button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Surface>
          </div>
        );
      })}

      {/* Legal text + audit trail */}
      <Surface className="p-5 mt-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-secondary mb-3">Legal & version chain</div>
        {rep.schema?.legal_text_blocks?.map((b, i) => (
          <div key={i} className="mb-3"><div className="text-sm font-medium" style={{ color: "var(--text)" }}>{b.title}</div><p className="text-sm text-secondary mt-0.5">{b.body}</p></div>
        ))}
        <div className="mt-2 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
          {(rep.version_chain || []).map((v, i) => (
            <div key={i} className="flex items-center gap-2 text-xs py-1">
              <span className="h-1.5 w-1.5 rounded-full accent-bg" />
              <span className="capitalize" style={{ color: "var(--text)" }}>{v.status.replace(/_/g, " ")}</span>
              <span className="text-secondary">· {new Date(v.at).toLocaleString("en-AU")} · {v.hash?.slice(0, 8)}</span>
            </div>
          ))}
          {(rep.signatures || []).map((s) => (
            <div key={s.id} className="flex items-center gap-2 text-xs py-1" style={{ color: "var(--success)" }}>
              <FileSignature size={12} /> Signed by {s.signer} ({s.signer_role}) · {new Date(s.at).toLocaleString("en-AU")}
            </div>
          ))}
        </div>
      </Surface>

      {/* Evidence bottom sheet */}
      <Sheet open={!!evidence} onOpenChange={(o) => !o && setEvidence(null)}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[80vh] overflow-y-auto" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <div className="mx-auto h-1.5 w-10 rounded-full mb-3" style={{ background: "var(--border)" }} />
          <SheetHeader><SheetTitle style={{ color: "var(--text)" }}>{evidence?.item.label} — evidence</SheetTitle></SheetHeader>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            {evidence?.media.map((m) => (
              <div key={m.id}>
                <img src={mediaUrl(m.signed_url)} className="w-full rounded-xl object-cover" alt="evidence" />
                <div className="mt-2 text-xs text-secondary space-y-0.5">
                  <div className="flex items-center gap-1"><Fingerprint size={11} /> SHA-256: {m.sha256?.slice(0, 24)}…</div>
                  <div>Captured: {new Date(m.captured_at).toLocaleString("en-AU")}</div>
                  <div>Device: {m.device_meta?.device || "Web capture"} · Room: {m.room}</div>
                </div>
              </div>
            ))}
            {evidence?.media.length === 0 && <p className="text-sm text-secondary">Evidence media not available.</p>}
          </div>
        </SheetContent>
      </Sheet>

      {/* Edit dialog */}
      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <DialogHeader><DialogTitle style={{ color: "var(--text)" }}>Edit — {editItem?.label}</DialogTitle></DialogHeader>
          {editItem && (
            <div className="space-y-3">
              <textarea value={editItem.comment} onChange={(e) => setEditItem({ ...editItem, comment: e.target.value })} rows={3}
                className="w-full text-sm rounded-lg px-3 py-2 outline-none border" style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }} data-testid="edit-comment" />
              <div className="flex flex-wrap gap-2">
                {Object.keys(editItem.condition).map((f) => (
                  <button key={f} onClick={() => setEditItem({ ...editItem, condition: { ...editItem.condition, [f]: !editItem.condition[f] } })}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold capitalize" style={{ background: editItem.condition[f] ? "var(--success)" : "var(--danger)", color: "#fff" }}>{f}</button>
                ))}
              </div>
              <AButton testid="save-edit" className="w-full" onClick={async () => { await review(editItem, "edit", { comment: editItem.comment, condition: editItem.condition, severity: editItem.severity }); setEditItem(null); }}>Save changes</AButton>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sign dialog */}
      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <DialogHeader><DialogTitle style={{ color: "var(--text)" }}>Approve & sign</DialogTitle></DialogHeader>
          <p className="text-sm text-secondary">Your signature binds to this exact report version (<span className="font-mono">{rep.version_hash?.slice(0, 12)}</span>). It becomes immutable and forms the legal record of human oversight.</p>
          <input value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Type your full name to sign" data-testid="sign-name"
            className="mt-3 w-full text-sm rounded-lg px-3 py-2.5 outline-none border" style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }} />
          <AButton testid="confirm-sign" className="w-full mt-3" disabled={signName.trim().length < 2} onClick={doSign}><ShieldCheck size={15} className="inline mr-1.5" /> Sign report</AButton>
        </DialogContent>
      </Dialog>
    </div>
  );
}
